import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/client/react';
import { generateClient } from 'aws-amplify/api';
import toast from 'react-hot-toast';
import { GET_MY_MESSAGES } from '../graphql/clientQueries';
import { SEND_MESSAGE, MARK_MESSAGE_AS_READ } from '../graphql/clientMutations';
import { GET_USER } from '../../graphql/queries';
import { useClientAuth } from '../hooks/useClientAuth';
import { useClientCases } from '../hooks/useClientCases';
import { useUnreadMessages } from '../hooks/useUnreadMessages';
import type { Message } from '../types';

interface GetMyMessagesResponse {
  listMyMessages: { items: Message[]; nextToken?: string };
}

interface GetUserResponse {
  getUser: { id: string; firstName: string; lastName: string; role?: string } | null;
}

interface SendMessageResponse {
  sendMessage: Message;
}

interface Conversation {
  staffId: string;
  staffName: string;
  staffRole: string;
  unreadCount: number;
  lastMessage?: Message;
  isAssigned: boolean;
}

export function ChatScreen() {
  const apolloClient = useApolloClient();
  const { user } = useClientAuth();
  const { cases } = useClientCases();
  const { decrementUnreadCount, refetch: refetchUnreadCount } = useUnreadMessages();

  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);
  const [messageText, setMessageText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [staffDetails, setStaffDetails] = useState<Map<string, { name: string; role: string }>>(new Map());
  const [staffDetailsLoaded, setStaffDetailsLoaded] = useState(false);

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const markedAsReadRef = useRef<Set<string>>(new Set());

  const { data, loading, error, refetch } = useQuery<GetMyMessagesResponse>(GET_MY_MESSAGES, {
    variables: { limit: 100 },
    fetchPolicy: 'cache-and-network',
  });

  const [sendMessageMutation] = useMutation(SEND_MESSAGE);
  const [markMessageAsRead] = useMutation(MARK_MESSAGE_AS_READ);

  // Subscribe to new messages using Amplify's GraphQL API (like staff side)
  useEffect(() => {
    if (!user?.id) return;

    const client = generateClient();
    
    const sub = client.graphql({
      query: `
        subscription OnMessageReceived($recipientId: ID!) {
          onMessageReceived(recipientId: $recipientId) {
            id
            conversationId
            clientId
            staffId
            senderId
            recipientId
            content
            isRead
            isEdited
            createdAt
          }
        }
      `,
      variables: { recipientId: user.id },
    });

    const subscription = (sub as any).subscribe({
      next: ({ data }: any) => {
        const msg = data?.onMessageReceived;
        if (msg) {
          apolloClient.cache.updateQuery<GetMyMessagesResponse>(
            { query: GET_MY_MESSAGES, variables: { limit: 100 } },
            (existing) => {
              if (!existing?.listMyMessages?.items) return existing;
              if (existing.listMyMessages.items.some((m) => m.id === msg.id)) return existing;
              return { listMyMessages: { ...existing.listMyMessages, items: [...existing.listMyMessages.items, msg] } };
            }
          );
          if (msg.senderId === selectedStaffId && msg.conversationId) {
            markMessageAsRead({ variables: { id: msg.id, conversationId: msg.conversationId, sentAt: msg.createdAt } }).catch(() => {});
          }
          refetchUnreadCount();
        }
      },
      error: (error: any) => {
        console.error('Message subscription error:', error);
      },
    });

    return () => subscription.unsubscribe();
  }, [user?.id, apolloClient, selectedStaffId, markMessageAsRead, refetchUnreadCount]);

  const messages = data?.listMyMessages?.items ?? [];

  const assignedStaffIds = useMemo(() => {
    const ids = new Set<string>();
    cases.forEach((c) => {
      if (!['CLOSED', 'SETTLED'].includes(c.status)) {
        if (c.assignedAttorneyId) ids.add(c.assignedAttorneyId);
        if (c.assignedCaseManagerId) ids.add(c.assignedCaseManagerId);
      }
    });
    return ids;
  }, [cases]);

  useEffect(() => {
    const staffIdsToFetch = new Set<string>();
    messages.forEach((m) => {
      if (m.staffId && !staffDetails.has(m.staffId)) staffIdsToFetch.add(m.staffId);
    });
    assignedStaffIds.forEach((id) => {
      if (!staffDetails.has(id)) staffIdsToFetch.add(id);
    });

    if (staffIdsToFetch.size === 0) {
      setStaffDetailsLoaded(true);
      return;
    }

    const fetchStaff = async () => {
      const newDetails = new Map(staffDetails);
      for (const staffId of staffIdsToFetch) {
        try {
          const { data: userData } = await apolloClient.query<GetUserResponse>({ query: GET_USER, variables: { id: staffId } });
          if (userData?.getUser) {
            const u = userData.getUser;
            newDetails.set(staffId, { name: `${u.firstName} ${u.lastName}`, role: u.role || 'Staff' });
          }
        } catch {
          newDetails.set(staffId, { name: 'Staff Member', role: 'Staff' });
        }
      }
      setStaffDetails(newDetails);
      setStaffDetailsLoaded(true);
    };
    fetchStaff();
  }, [messages, assignedStaffIds, apolloClient, staffDetails]);

  const conversations = useMemo<Conversation[]>(() => {
    const convMap = new Map<string, Conversation>();

    assignedStaffIds.forEach((staffId) => {
      const details = staffDetails.get(staffId);
      convMap.set(staffId, {
        staffId,
        staffName: details?.name || 'Loading...',
        staffRole: details?.role || 'Staff',
        unreadCount: 0,
        isAssigned: true,
      });
    });

    messages.forEach((m) => {
      const staffId = m.staffId;
      if (!staffId) return;

      const existing = convMap.get(staffId);
      const details = staffDetails.get(staffId);
      const isUnread = !m.isRead && m.senderId !== user?.id;

      if (existing) {
        if (!existing.lastMessage || new Date(m.createdAt) > new Date(existing.lastMessage.createdAt)) {
          existing.lastMessage = m;
        }
        if (isUnread) existing.unreadCount++;
      } else {
        convMap.set(staffId, {
          staffId,
          staffName: details?.name || 'Staff Member',
          staffRole: details?.role || 'Staff',
          unreadCount: isUnread ? 1 : 0,
          lastMessage: m,
          isAssigned: false,
        });
      }
    });

    return Array.from(convMap.values()).sort((a, b) => {
      if (a.isAssigned !== b.isAssigned) return a.isAssigned ? -1 : 1;
      const aTime = a.lastMessage?.createdAt || '';
      const bTime = b.lastMessage?.createdAt || '';
      return bTime.localeCompare(aTime);
    });
  }, [messages, assignedStaffIds, staffDetails, user?.id]);

  useEffect(() => {
    if (!selectedStaffId && conversations.length > 0 && staffDetailsLoaded) {
      setSelectedStaffId(conversations[0].staffId);
    }
  }, [conversations, selectedStaffId, staffDetailsLoaded]);

  const conversationMessages = useMemo(() => {
    if (!selectedStaffId) return [];
    return messages
      .filter((m) => m.staffId === selectedStaffId)
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  }, [messages, selectedStaffId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesContainerRef.current && conversationMessages.length > 0) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [conversationMessages]);

  useEffect(() => {
    if (!selectedStaffId || !user?.id) return;
    const unreadMessages = conversationMessages.filter(
      (m) => !m.isRead && m.senderId !== user.id && !markedAsReadRef.current.has(m.id)
    );
    unreadMessages.forEach((m) => {
      markedAsReadRef.current.add(m.id);
      markMessageAsRead({ variables: { id: m.id, conversationId: m.conversationId, sentAt: m.createdAt } })
        .then(() => decrementUnreadCount())
        .catch(() => markedAsReadRef.current.delete(m.id));
    });
  }, [conversationMessages, selectedStaffId, user?.id, markMessageAsRead, decrementUnreadCount]);

  const handleSend = useCallback(async () => {
    if (!messageText.trim() || !selectedStaffId || !user?.id || isSending) return;

    setIsSending(true);
    const text = messageText.trim();
    setMessageText('');

    try {
      const { data: result } = await sendMessageMutation({
        variables: { input: { recipientId: selectedStaffId, content: text } },
      });

      const sentMessage = (result as SendMessageResponse | null)?.sendMessage;
      if (sentMessage) {
        apolloClient.cache.updateQuery<GetMyMessagesResponse>(
          { query: GET_MY_MESSAGES, variables: { limit: 100 } },
          (existing) => {
            if (!existing?.listMyMessages?.items) return existing;
            if (existing.listMyMessages.items.some((m) => m.id === sentMessage.id)) return existing;
            return { listMyMessages: { ...existing.listMyMessages, items: [...existing.listMyMessages.items, sentMessage] } };
          }
        );
      }
    } catch {
      toast.error('Failed to send message');
      setMessageText(text);
    } finally {
      setIsSending(false);
      inputRef.current?.focus();
    }
  }, [messageText, selectedStaffId, user?.id, isSending, sendMessageMutation, apolloClient]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  const formatDateSeparator = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });
  };

  const shouldShowDateSeparator = (msg: Message, idx: number) => {
    if (idx === 0) return true;
    const prev = conversationMessages[idx - 1];
    return new Date(msg.createdAt).toDateString() !== new Date(prev.createdAt).toDateString();
  };

  // Loading state
  if (loading && !data) {
    return (
      <div className="h-[calc(100vh-10rem)] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-aalaw-red border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-gray-500">Loading messages...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="h-[calc(100vh-10rem)] flex items-center justify-center">
        <div className="text-center p-6">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-gray-700 font-medium mb-2">Unable to load messages</p>
          <button onClick={() => refetch()} className="text-aalaw-red hover:underline text-sm">Try again</button>
        </div>
      </div>
    );
  }

  // No conversations
  if (conversations.length === 0 && staffDetailsLoaded) {
    return (
      <div className="h-[calc(100vh-10rem)] flex items-center justify-center">
        <div className="text-center p-6 max-w-sm">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <h3 className="text-gray-800 font-medium mb-2">No conversations yet</h3>
          <p className="text-gray-500 text-sm">Once you have an active case, you'll be able to message your assigned attorney or case manager here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white sm:rounded-xl shadow-sm sm:border border-gray-200 flex flex-col h-[calc(100vh-8rem)] sm:h-[calc(100vh-10rem)] min-h-[300px]">
      {/* Conversation selector - compact on mobile, horizontal scroll on desktop */}
      <div className="flex-shrink-0 border-b border-gray-200 px-3 py-2">
        {/* Mobile: Dropdown selector */}
        <div className="sm:hidden">
          <select
            value={selectedStaffId || ''}
            onChange={(e) => setSelectedStaffId(e.target.value)}
            className="w-full px-3 py-2 bg-gray-100 border-0 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-aalaw-red"
          >
            {conversations.map((conv) => (
              <option key={conv.staffId} value={conv.staffId}>
                {conv.staffName} ({conv.staffRole}){conv.unreadCount > 0 ? ` • ${conv.unreadCount} new` : ''}
              </option>
            ))}
          </select>
        </div>
        
        {/* Desktop: Horizontal scroll */}
        <div className="hidden sm:flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {conversations.map((conv) => (
            <button
              key={conv.staffId}
              onClick={() => setSelectedStaffId(conv.staffId)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg flex-shrink-0 transition-colors ${
                conv.staffId === selectedStaffId
                  ? 'bg-aalaw-red text-white'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                conv.staffId === selectedStaffId ? 'bg-white/20' : 'bg-aalaw-red'
              }`}>
                <span className={`font-medium text-xs ${conv.staffId === selectedStaffId ? 'text-white' : 'text-white'}`}>
                  {conv.staffName.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </span>
              </div>
              <div className="text-left min-w-0">
                <p className="font-medium text-sm truncate max-w-[120px]">{conv.staffName}</p>
                <p className={`text-xs truncate max-w-[120px] ${conv.staffId === selectedStaffId ? 'text-red-100' : 'text-gray-500'}`}>
                  {conv.staffRole}
                </p>
              </div>
              {conv.unreadCount > 0 && conv.staffId !== selectedStaffId && (
                <span className="px-1.5 py-0.5 bg-aalaw-red text-white text-xs rounded-full ml-1">
                  {conv.unreadCount}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Messages area - scrollable */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto px-3 sm:px-4 py-2 sm:py-3 bg-gray-50 overscroll-contain scrollbar-visible min-h-0"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {conversationMessages.length === 0 ? (
          <div className="flex items-center justify-center h-full min-h-[200px]">
            <div className="text-center">
              <svg className="w-10 h-10 text-gray-300 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <p className="text-gray-400 text-sm">No messages yet</p>
            </div>
          </div>
        ) : (
          <div className="space-y-2 flex flex-col justify-end min-h-full">
            {conversationMessages.map((msg, idx) => {
              const isMe = msg.senderId === user?.id;
              const showDate = shouldShowDateSeparator(msg, idx);

              return (
                <div key={msg.id}>
                  {showDate && (
                    <div className="flex justify-center my-3">
                      <span className="px-3 py-1 bg-white text-gray-500 text-xs rounded-full shadow-sm border border-gray-100">
                        {formatDateSeparator(msg.createdAt)}
                      </span>
                    </div>
                  )}
                  <div className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] px-3 py-2 rounded-2xl ${
                      isMe ? 'bg-aalaw-red text-white rounded-br-sm' : 'bg-white text-gray-800 shadow-sm rounded-bl-sm'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                      <div className={`flex items-center gap-1 mt-0.5 ${isMe ? 'justify-end' : 'justify-start'}`}>
                        <span className={`text-[10px] ${isMe ? 'text-red-200' : 'text-gray-400'}`}>{formatTime(msg.createdAt)}</span>
                        {isMe && msg.isRead && (
                          <svg className="w-3 h-3 text-red-200" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Message input - fixed at bottom */}
      <div className="flex-shrink-0 border-t border-gray-200 p-2 sm:p-3 bg-white sm:rounded-b-xl">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            rows={1}
            className="flex-1 px-3 py-2 bg-gray-100 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-aalaw-red focus:bg-white transition-colors text-sm"
            style={{ maxHeight: '80px' }}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = 'auto';
              target.style.height = Math.min(target.scrollHeight, 80) + 'px';
            }}
          />
          <button
            onClick={handleSend}
            disabled={!messageText.trim() || isSending}
            className="flex-shrink-0 w-10 h-10 bg-aalaw-red hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-full flex items-center justify-center transition-colors"
          >
            {isSending ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
